import React, { useState, useRef, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Radio,
  RadioGroup,
  Checkbox,
  CheckboxGroup,
  Input,
  Textarea,
  Box,
  Badge,
  Flex,
  Button,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { SurveyQuestion as SurveyQuestionType } from './types';
import { useTranslation } from 'react-i18next';
import {
  OTHER_OPTION_VALUE,
  getSelectedOptions,
  getOtherText,
  createSingleChoiceResponse,
  createMultipleChoiceResponse,
  MIN_OPTIONS_FOR_DROPDOWN_VIEW,
  OtherResponse,
  MultipleChoiceWithOther,
} from './utils';

type SurveyResponseValue = 
  | string 
  | number 
  | OtherResponse 
  | MultipleChoiceWithOther 
  | string[] 
  | null 
  | undefined;

interface SurveyQuestionProps {
  question: SurveyQuestionType;
  value: SurveyResponseValue;
  onChange: (value: SurveyResponseValue) => void;
  error?: string;
  userRoles: string[];
}

const CHECKBOX_INLINE_STYLES: React.CSSProperties = {
  marginRight: '8px',
  width: '16px',
  height: '16px',
  cursor: 'pointer',
  accentColor: '#667eea',
};

const SurveyQuestion: React.FC<SurveyQuestionProps> = ({
  question,
  value,
  onChange,
  error,
  userRoles,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Check if question is relevant for user's roles
  const isRelevant = question.target_roles.length === 0 || 
    question.target_roles.some(role => {
      const roleStr = role.toString();
      // If target role is 'all', show to everyone
      if (roleStr === 'all') return true;
      // Otherwise check if user has this specific role
      return userRoles.includes(roleStr);
    });

  if (!isRelevant) {
    return null;
  }
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const renderDropdownCheckboxItem = (
    optionValue: string,
    label: string,
    isChecked: boolean,
    selected: string[],
    otherText: string,
    onToggle: (newSelected: string[], textToUse?: string) => void
  ) => (
    <Box
      key={optionValue}
      as="label"
      cursor="pointer"
      display="flex"
      alignItems="center"
      py={2}
      px={3}
      _hover={{ bg: 'purple.50' }}
      transition="background 0.2s"
    >
      <input
        type="checkbox"
        checked={isChecked}
        onChange={(e) => {
          const newSelected = e.target.checked
            ? [...selected, optionValue]
            : selected.filter((s) => s !== optionValue);
          const hasOther = newSelected.includes(OTHER_OPTION_VALUE);
          const textToUse = hasOther ? (otherText || '') : undefined;
          onToggle(newSelected, textToUse);
        }}
        style={CHECKBOX_INLINE_STYLES}
      />
      <Text fontWeight="400" color="gray.700" fontSize="sm" userSelect="none">
        {label}
      </Text>
    </Box>
  );

  const renderDropdownRadioItem = (
    optionValue: string,
    label: string,
    isSelected: boolean,
    otherText: string,
    onSelect: (option: string, textToUse?: string) => void
  ) => (
    <Box
      key={optionValue}
      as="label"
      cursor="pointer"
      display="flex"
      alignItems="center"
      py={2}
      px={3}
      _hover={{ bg: 'purple.50' }}
      transition="background 0.2s"
    >
      <input
        type="radio"
        checked={isSelected}
        onChange={() => {
          const textToUse = optionValue === OTHER_OPTION_VALUE ? (otherText || '') : undefined;
          onSelect(optionValue, textToUse);
        }}
        style={CHECKBOX_INLINE_STYLES}
      />
      <Text fontWeight="400" color="gray.700" fontSize="sm" userSelect="none">
        {label}
      </Text>
    </Box>
  );

  const renderQuestionInput = () => {
    switch (question.question_type) {
      case 'rating':
        return (
          <RadioGroup value={value?.toString() || ''} onChange={(val) => onChange(parseInt(val))}>
            <VStack spacing={4} w="100%">
            <Box
              p={{ base: 3, sm: 4 }}
              bg="purple.50"
              borderRadius={{ base: "lg", sm: "xl" }}
              border="1px solid"
              borderColor="purple.100"
              w="100%"
            >
              <Text fontSize={{ base: "xs", sm: "sm" }} color="purple.700" fontWeight="500" textAlign="center">
                {t('survey.rateYourAgreement')} 1 ({question.options[0] || t('survey.stronglyDisagree')}) {t('survey.to')} 5 ({question.options[4] || t('survey.stronglyAgree')})
              </Text>
            </Box>
              {/* Mobile/Tablet: Stack vertically */}
              <VStack spacing={3} display={{ base: 'flex', md: 'none' }} w="100%">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Radio 
                    key={rating} 
                    value={rating.toString()} 
                    colorScheme="purple"
                    w="100%"
                    _checked={{
                      '& .chakra-radio__control': {
                        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: 'transparent',
                      }
                    }}
                  >
                    <HStack spacing={4} w="100%" p={3} borderRadius="xl" _hover={{ bg: 'purple.50' }} transition="all 0.2s">
                      <Box
                        w={10}
                        h={10}
                        borderRadius="full"
                        bg={value === rating ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'gray.100'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        transition="all 0.2s"
                        flexShrink={0}
                      >
                        <Text fontSize="md" fontWeight="bold" color={value === rating ? 'white' : 'gray.600'}>
                          {rating}
                        </Text>
                      </Box>
                      <Text fontSize="sm" color="gray.600" fontWeight="500" textAlign="left">
                        {rating === 1 && (question.options[0] || t('survey.stronglyDisagree'))}
                        {rating === 2 && (question.options[1] || t('survey.disagree'))}
                        {rating === 3 && (question.options[2] || t('survey.neutral'))}
                        {rating === 4 && (question.options[3] || t('survey.agree'))}
                        {rating === 5 && (question.options[4] || t('survey.stronglyAgree'))}
                      </Text>
                    </HStack>
                  </Radio>
                ))}
              </VStack>
              
              {/* Desktop: Horizontal layout */}
              <HStack spacing={{ base: 2, lg: 4 }} justify="center" display={{ base: 'none', md: 'flex' }} w="100%">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Radio 
                    key={rating} 
                    value={rating.toString()} 
                    colorScheme="purple"
                    _checked={{
                      '& .chakra-radio__control': {
                        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: 'transparent',
                        transform: 'scale(1.1)',
                      }
                    }}
                  >
                    <VStack spacing={2} p={{ base: 2, lg: 3 }} borderRadius="xl" _hover={{ bg: 'purple.50' }} transition="all 0.2s">
                      <Box
                        w={{ base: 10, lg: 12 }}
                        h={{ base: 10, lg: 12 }}
                        borderRadius="full"
                        bg={value === rating ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'gray.100'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        transition="all 0.2s"
                        _hover={{ transform: 'scale(1.05)' }}
                      >
                        <Text fontSize={{ base: 'md', lg: 'lg' }} fontWeight="bold" color={value === rating ? 'white' : 'gray.600'}>
                          {rating}
                        </Text>
                      </Box>
                      <Text fontSize={{ base: 'xs', lg: 'sm' }} textAlign="center" color="gray.600" fontWeight="500" maxW={{ base: '60px', lg: '80px' }}>
                        {rating === 1 && (question.options[0] || t('survey.stronglyDisagree'))}
                        {rating === 2 && (question.options[1] || t('survey.disagree'))}
                        {rating === 3 && (question.options[2] || t('survey.neutral'))}
                        {rating === 4 && (question.options[3] || t('survey.agree'))}
                        {rating === 5 && (question.options[4] || t('survey.stronglyAgree'))}
                      </Text>
                    </VStack>
                  </Radio>
                ))}
              </HStack>
            </VStack>
          </RadioGroup>
        );

      case 'multiple_choice': {
        const selected = getSelectedOptions(value, 'multiple_choice');
        const otherText = getOtherText(value, 'multiple_choice');
        const isOtherSelected = selected.includes(OTHER_OPTION_VALUE);
        const totalOptions = question.options.length + (question.allow_other_option ? 1 : 0);
        const useDropdown = totalOptions >= MIN_OPTIONS_FOR_DROPDOWN_VIEW;
        
        if (useDropdown) {
          return (
            <VStack align="stretch" spacing={3} w="100%" position="relative">
              <Button
                ref={buttonRef}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                rightIcon={
                  <Box
                    as={ChevronDownIcon}
                    style={{
                      transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                }
                w="100%"
                textAlign="left"
                justifyContent="space-between"
                borderRadius="xl"
                border="2px solid"
                borderColor={error ? 'red.300' : 'gray.200'}
                bg="white"
                minH="48px"
                h="auto"
                py={3}
                px={4}
                _hover={{ 
                  borderColor: error ? 'red.400' : 'purple.300',
                  bg: 'purple.50'
                }}
                _focus={{ 
                  borderColor: 'purple.400', 
                  boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                  bg: 'white'
                }}
                fontSize="md"
                fontWeight="400"
                transition="all 0.2s"
              >
                <Text 
                  w="100%" 
                  textAlign="left" 
                  overflow="hidden" 
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {selected.length === 0 
                    ? t('survey.selectOptions') || 'Select options...'
                    : selected.length === 1
                    ? selected[0] === OTHER_OPTION_VALUE 
                      ? t('survey.somethingElse') || 'Something else'
                      : selected[0]
                    : `${selected.length} ${t('survey.optionsSelected') || 'options selected'}`
                  }
                </Text>
              </Button>
              
              {isDropdownOpen && (
                <Box
                  ref={dropdownRef}
                  position="absolute"
                  top="calc(100% + 4px)"
                  left={0}
                  right={0}
                  zIndex={9999}
                  maxH="min(300px, calc(100vh - 200px))"
                  overflowY="auto"
                  overflowX="hidden"
                  borderRadius="xl"
                  border="2px solid"
                  borderColor="gray.200"
                  boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
                  bg="white"
                  py={2}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                    },
                  }}
                >
                  <VStack align="stretch" spacing={0} px={2} pb={2}>
                      {question.options.map((option) => {
                        const isChecked = selected.includes(option);
                        return renderDropdownCheckboxItem(
                          option,
                          option,
                          isChecked,
                          selected,
                          otherText,
                          (newSelected, textToUse) => {
                            const newResponse = createMultipleChoiceResponse(newSelected, textToUse);
                            onChange(newResponse);
                          }
                        );
                      })}
                      {question.allow_other_option && renderDropdownCheckboxItem(
                        OTHER_OPTION_VALUE,
                        t('survey.somethingElse') || 'Something else',
                        selected.includes(OTHER_OPTION_VALUE),
                        selected,
                        otherText,
                        (newSelected, textToUse) => {
                          const newResponse = createMultipleChoiceResponse(newSelected, textToUse);
                          onChange(newResponse);
                        }
                      )}
                    </VStack>
                </Box>
              )}
              {question.allow_other_option && isOtherSelected && (
                <Box mt={2}>
                  <Input
                    value={otherText}
                    onChange={(e) => {
                      const newText = e.target.value;
                      onChange(createMultipleChoiceResponse(selected, newText));
                    }}
                    placeholder={t('survey.pleaseSpecify') || 'Please specify...'}
                    borderRadius="md"
                    minH="30px"
                    border="2px solid"
                    borderColor={error ? 'red.300' : 'gray.200'}
                    bg="white"
                    _focus={{ 
                      borderColor: 'purple.400', 
                      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                      bg: 'white'
                    }}
                    _hover={{ borderColor: error ? 'red.400' : 'gray.300' }}
                    fontSize="md"
                    fontWeight="400"
                    transition="all 0.2s"
                  />
                </Box>
              )}
            </VStack>
          );
        }
        
        return (
          <VStack align="stretch" spacing={3}>
            <CheckboxGroup 
              value={selected} 
              onChange={(newSelected) => {
                const selectedStrings = newSelected.map(String);
                const hasOther = selectedStrings.includes(OTHER_OPTION_VALUE);
                const textToUse = hasOther ? (otherText || '') : undefined;
                const newResponse = createMultipleChoiceResponse(selectedStrings, textToUse);
                onChange(newResponse);
              }}
            >
              <VStack align="stretch" spacing={3}>
                {question.options.map((option) => (
                  <Checkbox 
                    key={option} 
                    value={option} 
                    colorScheme="purple"
                    size="lg"
                    p={4}
                    borderRadius="xl"
                    border="2px solid"
                    borderColor="gray.200"
                    bg="white"
                    _hover={{ 
                      borderColor: 'purple.300',
                      bg: 'purple.50',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                    _checked={{
                      borderColor: 'purple.500',
                      bg: 'purple.50',
                      '& .chakra-checkbox__control': {
                        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: 'transparent',
                      }
                    }}
                    transition="all 0.2s"
                  >
                    <Text fontWeight="500" color="gray.700">
                      {option}
                    </Text>
                  </Checkbox>
                ))}
                {question.allow_other_option && (
                  <Checkbox 
                    value={OTHER_OPTION_VALUE} 
                    colorScheme="purple"
                    size="lg"
                    p={4}
                    borderRadius="xl"
                    border="2px solid"
                    borderColor="gray.200"
                    bg="white"
                    _hover={{ 
                      borderColor: 'purple.300',
                      bg: 'purple.50',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                    _checked={{
                      borderColor: 'purple.500',
                      bg: 'purple.50',
                      '& .chakra-checkbox__control': {
                        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: 'transparent',
                      }
                    }}
                    transition="all 0.2s"
                  >
                    <Text fontWeight="500" color="gray.700">
                      {t('survey.somethingElse') || 'Something else'}
                    </Text>
                  </Checkbox>
                )}
              </VStack>
            </CheckboxGroup>
            {question.allow_other_option && isOtherSelected && (
              <Box mt={2}>
                <Input
                  value={otherText}
                  onChange={(e) => {
                    const newText = e.target.value;
                    onChange(createMultipleChoiceResponse(selected, newText));
                  }}
                  placeholder={t('survey.pleaseSpecify') || 'Please specify...'}
                  borderRadius="md"
                  minH="30px"
                  border="2px solid"
                  borderColor={error ? 'red.300' : 'gray.200'}
                  bg="white"
                  _focus={{ 
                    borderColor: 'purple.400', 
                    boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                    bg: 'white'
                  }}
                  _hover={{ borderColor: error ? 'red.400' : 'gray.300' }}
                  fontSize="md"
                  fontWeight="400"
                  transition="all 0.2s"
                />
              </Box>
            )}
          </VStack>
        );
      }

      case 'yes_no':
        return (
          <RadioGroup value={typeof value === 'string' ? value : ''} onChange={onChange}>
            <HStack spacing={{ base: 4, md: 8 }} justify="center" w="100%">
              {[
                { value: 'yes', label: t('survey.yes'), color: 'green' },
                { value: 'no', label: t('survey.no'), color: 'red' }
              ].map(({ value: optionValue, label, color }) => (
                <Radio 
                  key={optionValue} 
                  value={optionValue} 
                  colorScheme={color}
                  size="lg"
                >
                  <VStack spacing={2}>
                    <Box
                      w={{ base: 12, md: 16 }}
                      h={{ base: 12, md: 16 }}
                      borderRadius="full"
                      bg={value === optionValue ? 
                        (color === 'green' ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)' : 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)') 
                        : 'gray.100'
                      }
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      transition="all 0.2s"
                      _hover={{ transform: 'scale(1.05)' }}
                    >
                      <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={value === optionValue ? 'white' : 'gray.600'}>
                        {label}
                      </Text>
                    </Box>
                  </VStack>
                </Radio>
              ))}
            </HStack>
          </RadioGroup>
        );

      case 'text':
        return (
          <Textarea
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('survey.shareYourFeedback')}
            minH="120px"
            borderRadius="xl"
            border="2px solid"
            borderColor={error ? 'red.300' : 'gray.200'}
            bg="white"
            _focus={{ 
              borderColor: 'purple.400', 
              boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
              bg: 'white'
            }}
            _hover={{ borderColor: error ? 'red.400' : 'gray.300' }}
            fontSize="md"
            fontWeight="400"
            transition="all 0.2s"
          />
        );

      case 'numeric':
        return (
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={value !== undefined && value !== null ? value.toString() : ''}
            onChange={(e) => {
              const inputValue = e.target.value.replace(/[^0-9]/g, '');
              if (inputValue === '') {
                onChange(undefined);
                return;
              }
              const numValue = parseInt(inputValue, 10);
              if (!isNaN(numValue) && numValue >= 0) {
                onChange(numValue);
              }
            }}
            placeholder="0"
            borderRadius="xl"
            border="2px solid"
            borderColor={error ? 'red.300' : 'gray.200'}
            bg="white"
            _focus={{ 
              borderColor: 'purple.400', 
              boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
              bg: 'white'
            }}
            _hover={{ borderColor: error ? 'red.400' : 'gray.300' }}
            fontSize="md"
            fontWeight="400"
            transition="all 0.2s"
            w="100%"
            maxW="300px"
          />
        );

      case 'single_choice': {
        const selected = getSelectedOptions(value, 'single_choice');
        const selectedOption = selected[0] || '';
        const otherText = getOtherText(value, 'single_choice');
        const isOtherSelected = selectedOption === OTHER_OPTION_VALUE;
        const displayValue = isOtherSelected ? OTHER_OPTION_VALUE : selectedOption;
        const totalOptions = question.options.length + (question.allow_other_option ? 1 : 0);
        const useDropdown = totalOptions >= MIN_OPTIONS_FOR_DROPDOWN_VIEW;
        
        if (useDropdown) {
          return (
            <VStack align="stretch" spacing={3} w="100%" position="relative">
              <Button
                ref={buttonRef}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                rightIcon={
                  <Box
                    as={ChevronDownIcon}
                    style={{
                      transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                }
                w="100%"
                textAlign="left"
                justifyContent="space-between"
                borderRadius="xl"
                border="2px solid"
                borderColor={error ? 'red.300' : 'gray.200'}
                bg="white"
                minH="48px"
                h="auto"
                py={3}
                px={4}
                _hover={{ 
                  borderColor: error ? 'red.400' : 'purple.300',
                  bg: 'purple.50'
                }}
                _focus={{ 
                  borderColor: 'purple.400', 
                  boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                  bg: 'white'
                }}
                fontSize="md"
                fontWeight="400"
                transition="all 0.2s"
              >
                <Text 
                  w="100%" 
                  textAlign="left" 
                  overflow="hidden" 
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {selectedOption === '' 
                    ? t('survey.selectOption') || 'Please select from dropdown'
                    : selectedOption === OTHER_OPTION_VALUE 
                    ? t('survey.somethingElse') || 'Something else'
                    : selectedOption
                  }
                </Text>
              </Button>
              
              {isDropdownOpen && (
                <Box
                  ref={dropdownRef}
                  position="absolute"
                  top="calc(100% + 4px)"
                  left={0}
                  right={0}
                  zIndex={9999}
                  maxH="min(300px, calc(100vh - 200px))"
                  overflowY="auto"
                  overflowX="hidden"
                  borderRadius="xl"
                  border="2px solid"
                  borderColor="gray.200"
                  boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
                  bg="white"
                  py={2}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                    },
                  }}
                >
                  <VStack align="stretch" spacing={0} px={2} pb={2}>
                    {question.options.map((option) => (
                      renderDropdownRadioItem(
                        option,
                        option,
                        selectedOption === option,
                        otherText,
                        (newOption, textToUse) => {
                          const newResponse = createSingleChoiceResponse(newOption, textToUse);
                          onChange(newResponse);
                          setIsDropdownOpen(false);
                        }
                      )
                    ))}
                    {question.allow_other_option && renderDropdownRadioItem(
                      OTHER_OPTION_VALUE,
                      t('survey.somethingElse') || 'Something else',
                      isOtherSelected,
                      otherText,
                      (newOption, textToUse) => {
                        const newResponse = createSingleChoiceResponse(newOption, textToUse);
                        onChange(newResponse);
                        setIsDropdownOpen(false);
                      }
                    )}
                  </VStack>
                </Box>
              )}
              {question.allow_other_option && isOtherSelected && (
                <Box mt={2}>
                  <Input
                    value={otherText}
                    onChange={(e) => {
                      const newText = e.target.value;
                      onChange(createSingleChoiceResponse(OTHER_OPTION_VALUE, newText));
                    }}
                    placeholder={t('survey.pleaseSpecify') || 'Please specify...'}
                    borderRadius="md"
                    minH="30px"
                    border="2px solid"
                    borderColor={error ? 'red.300' : 'gray.200'}
                    bg="white"
                    _focus={{ 
                      borderColor: 'purple.400', 
                      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                      bg: 'white'
                    }}
                    _hover={{ borderColor: error ? 'red.400' : 'gray.300' }}
                    fontSize="md"
                    fontWeight="400"
                    transition="all 0.2s"
                  />
                </Box>
              )}
            </VStack>
          );
        }
        
        return (
          <VStack spacing="2" w="100%" display="flex" alignItems="flex-start">
            <RadioGroup 
              value={displayValue} 
              onChange={(newOption) => {
                const textToUse = newOption === OTHER_OPTION_VALUE ? (otherText || '') : undefined;
                const newResponse = createSingleChoiceResponse(newOption, textToUse);
                onChange(newResponse);
              }}
            >
              <VStack spacing="2" w="100%" display="flex" alignItems="flex-start">
                {question.options.map((option) => (
                  <Radio 
                    key={option} 
                    value={option} 
                    colorScheme="purple"
                    size="lg"
                  >
                    <VStack spacing={2}>
                      <Box
                        w="100%"
                        h={{ base: 6, md: 8 }}
                        bg={selectedOption === option ? 
                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : ''
                        }
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="md"
                        p="0 5px"
                        transition="all 0.2s"
                        _hover={{ transform: 'scale(1.05)' }}
                      >
                        <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={selectedOption === option ? 'white' : 'gray.600'}>
                          {option}
                        </Text>
                      </Box>
                    </VStack>
                  </Radio>
                ))}
                {question.allow_other_option && (
                  <Radio 
                    key={OTHER_OPTION_VALUE} 
                    value={OTHER_OPTION_VALUE} 
                    colorScheme="purple"
                    size="lg"
                  >
                    <VStack spacing={2}>
                      <Box
                        w="100%"
                        h={{ base: 6, md: 8 }}
                        bg={isOtherSelected ? 
                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : ''
                        }
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="md"
                        p="0 5px"
                        transition="all 0.2s"
                        _hover={{ transform: 'scale(1.05)' }}
                      >
                        <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={isOtherSelected ? 'white' : 'gray.600'}>
                          {t('survey.somethingElse') || 'Something else'}
                        </Text>
                      </Box>
                    </VStack>
                  </Radio>
                )}
              </VStack>
            </RadioGroup>
            {question.allow_other_option && isOtherSelected && (
              <Box mt={2} w="100%">
                <Input
                  value={otherText}
                  onChange={(e) => {
                    const newText = e.target.value;
                    onChange(createSingleChoiceResponse(OTHER_OPTION_VALUE, newText));
                  }}
                  placeholder={t('survey.pleaseSpecify') || 'Please specify...'}
                  borderRadius="xl"
                  border="2px solid"
                  borderColor={error ? 'red.300' : 'gray.200'}
                  bg="white"
                  _focus={{ 
                    borderColor: 'purple.400', 
                    boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                    bg: 'white'
                  }}
                  _hover={{ borderColor: error ? 'red.400' : 'gray.300' }}
                  fontSize="md"
                  fontWeight="400"
                  transition="all 0.2s"
                />
              </Box>
            )}
          </VStack>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Box
      p={{ base: 4, sm: 6, md: 8 }}
      border="2px solid"
      borderColor={error ? 'red.200' : 'gray.100'}
      borderRadius={{ base: "xl", sm: "2xl" }}
      bg={error ? 'red.50' : 'white'}
      h="100%"
      minH="100%"
      flex={1}
      display="flex"
      flexDirection="column"
      _hover={{ 
        borderColor: error ? 'red.300' : 'purple.200',
        transform: 'translateY(-2px)',
        boxShadow: error ? '0 8px 25px rgba(239, 68, 68, 0.15)' : '0 8px 25px rgba(102, 126, 234, 0.15)'
      }}
      transition="all 0.3s ease"
      position="relative"
      overflow="visible"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        bg: error ? 'linear-gradient(90deg, #f87171 0%, #ef4444 100%)' : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        opacity: error ? 1 : 0,
        transition: 'opacity 0.3s ease'
      }}
    >
      <VStack align="start" spacing={{ base: 4, sm: 6 }} h="100%" flex={1}>
        <Flex justify="space-between" align="start" w="100%" direction={{ base: "column", sm: "row" }} gap={{ base: 2, sm: 0 }}>
          <Text 
            fontSize={{ base: "lg", sm: "xl" }} 
            fontWeight="600" 
            color="gray.800"
            lineHeight="1.4"
            letterSpacing="-0.01em"
            flex={1}
          >
            {question.question_text}
          </Text>
          {question.is_required && (
            <Badge 
              colorScheme="red" 
              variant="solid" 
              fontSize="xs"
              borderRadius="full"
              px={3}
              py={1}
              fontWeight="600"
              bg="linear-gradient(135deg, #f87171 0%, #ef4444 100%)"
              alignSelf={{ base: "flex-start", sm: "flex-start" }}
            >
              {t('survey.required')}
            </Badge>
          )}
        </Flex>

        <Box flex={1} w="100%" display="flex" flexDirection="column" justifyContent="flex-start">
          {renderQuestionInput()}
        </Box>

        {error && (
          <Box
            p={{ base: 3, sm: 4 }}
            bg="red.50"
            borderRadius={{ base: "lg", sm: "xl" }}
            border="1px solid"
            borderColor="red.200"
            w="100%"
          >
            <Text fontSize={{ base: "xs", sm: "sm" }} color="red.600" fontWeight="500" display="flex" alignItems="center">
              <Box w={2} h={2} bg="red.500" borderRadius="full" mr={2} />
              {error}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default SurveyQuestion; 